import { EventEmitter } from 'events';
import Immutable, { Map } from 'immutable';
import _ from 'lodash';

import AppDispatcher from '../dispatcher/AppDispatcher';
import LocalStorageHelper from '../helpers/LocalStorage';
import { resolve } from '../helpers/LinkMapper';

const CHANGE_EVENT = 'CHANGE';

// Weakmap used to implement private members
const EMITTERS = new WeakMap();
export const PersistedStores = [];

/**
 * [defaultOptions description]
 * @type {Object}
 */
const defaultOptions = {
    // localStorage key to persist store data
    storageKey: null,

    // Immutable Record class with a `fromJSON` static method
    recordType: null,

    // Max number of seconds to keep persisted store data
    expires: null,

    // Function to validate persisted store data that hasn't expired
    validateStoredData: null
};

class BaseStore {

    constructor(options = defaultOptions) {
        Object.assign(this, _.pick(options, _.keys(defaultOptions)));
        let emitter = new EventEmitter();
        // EventEmitter started to throw a warning
        // about a potential memory leak (11 listeners)
        emitter.setMaxListeners(Infinity);
        EMITTERS.set(this, emitter);
        this.registerActionHandlers();
        this.state = this.getInitialState();
        if (options.storageKey) {
            PersistedStores.push(this);
        }
    }

    get(key) {
        return this.state.get(key);
    }

    getAll() {
        return this.state;
    }

    getInitialState () {
        return this.deserialize(this.recordType ? resolve(null).as(this.recordType) : null);
    }

    getPersistedState () {
        if (this.storageKey) {
            const local = LocalStorageHelper.objectFromLocalStorage(
                this.storageKey,
                this.recordType,
                this.validateStoredData
            );

            if (local) {
                return this.deserialize(local);
            }
        }

        return null;
    }

    deserialize (serialized) {
        const RecordType = this.recordType || Map;
        return serialized ? Immutable.fromJS(serialized) : new RecordType();
    }

    setState (newState) {
        this.state = newState;
        this.persist();
        this.emitChange();
    }

    loadPersistedState () {
        const persistedState = this.getPersistedState();
        this.setState(persistedState ? persistedState : this.state);
    }

    resetState () {
        this.setState(this.getInitialState());
    }

    getStorageKey () {
        return this.storageKey;
    }

    getExpiry () {
        return LocalStorageHelper.getExpiry(this.getStorageKey());
    }

    persist () {
        if (this.storageKey) {
            LocalStorageHelper.objectToLocalStorage(this.getStorageKey(), this.serialize(), this.expires);
        }
    }

    serialize () {
        return this.state.toJS();
    }

    getActionHandlers () { return false; }

    registerActionHandlers () {
        const handlers = this.getActionHandlers();
        if (handlers) {
            AppDispatcher.register((payload) => {
                return handlers[payload.actionType] && handlers[payload.actionType].call(this, payload);
            });
        }
    }

    emitChange () {
        EMITTERS.get(this).emit(CHANGE_EVENT);
    }

    addChangeListener (callback) {
        EMITTERS.get(this).on(CHANGE_EVENT, callback);
    }

    removeChangeListener (callback) {
        EMITTERS.get(this).removeListener(CHANGE_EVENT, callback);
    }


}

export default BaseStore;



// WEBPACK FOOTER //
// ./src/stores/BaseStore.js